/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Product CRUD Operations
 * 
 * Handles product creation, retrieval, update, and deletion.
 */

import type { ProductsResponseData, ProductResponseData } from '@shared/types/responses/products';
import type { ProductMetadata } from '@shared/types/products';
import { authenticatedFetch } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';
import { writeProductMetadata } from './product-metadata';

/**
 * Get all products from the database
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function getProducts(searchQuery?: string): Promise<ProductsResponseData> {
  try {
    const endpoint = searchQuery 
      ? `/api/products?q=${encodeURIComponent(searchQuery)}`
      : '/api/products';
    
    const response = await authenticatedFetch(endpoint);
    return await handleApiResponse<ProductsResponseData>(response);
  } catch (error: any) {
    console.error('[SkillShop Service] Error fetching products:', error);
    throw error;
  }
}

/**
 * Get products filtered by organization ID
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function getProductsByOrganization(organizationId: string): Promise<ProductsResponseData> {
  try {
    const response = await authenticatedFetch(`/api/products/by-organization/${organizationId}`);
    return await handleApiResponse<ProductsResponseData>(response);
  } catch (error: any) {
    console.error('[SkillShop Service] Error fetching products by organization:', error);
    throw error;
  }
}

/**
 * Get a single product by UUID
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function getProduct(id: string): Promise<ProductResponseData | null> {
  try {
    const response = await authenticatedFetch(`/api/products/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to fetch product (${response.status}): ${response.statusText}`);
    }

    return await handleApiResponse<ProductResponseData>(response);
  } catch (error: any) {
    console.error('[SkillShop Service] Error fetching product:', error);
    throw error;
  }
}

/**
 * Helper: Convert ProductResponseData to ProductMetadata and write to file
 */
async function writeProductMetadataFromProduct(product: ProductResponseData): Promise<void> {
  if (!product.repositoryUrl) {
    return;
  }

  const metadata: ProductMetadata = {
    id: product.id,
    productId: product.productId,
    organizationId: product.organizationId,
    title: product.title,
    description: product.description,
    owner: product.owner,
    repositoryUrl: product.repositoryUrl,
    repoPath: product.repoPath ?? null,
    capabilities: product.capabilities || [],
    users: product.users || [],
    links: product.links || [],
    architectureDiagram: product.architectureDiagram ?? null,
    createdAt: typeof product.createdAt === 'string' 
      ? product.createdAt 
      : new Date(product.createdAt).toISOString(),
    updatedAt: typeof product.updatedAt === 'string' 
      ? product.updatedAt 
      : new Date(product.updatedAt).toISOString(),
  };

  await writeProductMetadata(
    product.repositoryUrl,
    metadata,
    product.repoPath,
    `Update product metadata: ${product.title}`
  );
}

/**
 * Create a new product
 * SkillShop server endpoint - uses authenticatedFetch
 * Also writes product.md to the repo if repositoryUrl is provided
 */
export async function createProduct(product: Record<string, unknown>): Promise<ProductResponseData> {
  try {
    const response = await authenticatedFetch('/api/products', {
      method: 'POST',
      body: product,
    });

    const createdProduct = await handleApiResponse<ProductResponseData>(response);

    // If the product has a repository URL, also write product.md
    if (createdProduct.repositoryUrl) {
      try {
        await writeProductMetadataFromProduct(createdProduct);
        console.log('[SkillShop Service] Product metadata written to repo');
      } catch (writeError: any) {
        // Log but don't fail the create - database is still the source during migration
        console.warn('[SkillShop Service] Failed to write product.md:', writeError.message);
      }
    }

    return createdProduct;
  } catch (error: any) {
    console.error('[SkillShop Service] Error creating product:', error);
    throw error;
  }
}

/**
 * Update a product
 * SkillShop server endpoint - uses authenticatedFetch
 * Also updates product.md in the repo if repositoryUrl is provided
 */
export async function updateProduct(id: string, updates: Record<string, unknown>): Promise<ProductResponseData> {
  try {
    const response = await authenticatedFetch(`/api/products/${id}`, {
      method: 'PUT',
      body: updates,
    });

    const updatedProduct = await handleApiResponse<ProductResponseData>(response);

    // If the product has a repository URL, also update product.md
    if (updatedProduct.repositoryUrl) {
      try {
        await writeProductMetadataFromProduct(updatedProduct);
        console.log('[SkillShop Service] Product metadata updated in repo');
      } catch (writeError: any) {
        // Log but don't fail the update - database is still the source during migration
        console.warn('[SkillShop Service] Failed to update product.md:', writeError.message);
      }
    }

    return updatedProduct;
  } catch (error: any) {
    console.error('[SkillShop Service] Error updating product:', error);
    throw error;
  }
}

/**
 * Delete a product by UUID
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function deleteProduct(id: string): Promise<boolean> {
  try {
    const response = await authenticatedFetch(`/api/products/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete product (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    return result.success;
  } catch (error: any) {
    console.error('[SkillShop Service] Error deleting product:', error);
    throw error;
  }
}

